// PanoramaRenderer — RoomRenderer adapter around the Sprint 1 PanoramaSphere.
//
// Keeps the existing inverted-sphere + cached texture pipeline intact; just
// wraps it in the strategy interface so App can dispatch between this and the
// gsplat / glTF renderers without special-casing panorama as the default.
//
// The sphere stays alive for the lifetime of the app — hide() just disables
// the entity (zero per-frame cost). Re-show with a different texture is
// instant when the texture is already in AssetLoader's cache.

import * as pc from 'playcanvas';
import { PanoramaSphere } from './panoramaSphere';
import { AssetLoader } from './assetLoader';
import type { Room, Variant } from '../app/types';
import type { RoomRenderer } from './roomRenderer';

export class PanoramaRenderer implements RoomRenderer {
  readonly sphere: PanoramaSphere;
  private readonly assets: AssetLoader;

  constructor(app: pc.Application, assets: AssetLoader) {
    this.sphere = new PanoramaSphere(app);
    this.assets = assets;
  }

  async show(_room: Room, variant: Variant): Promise<void> {
    const { texture, isFallback } = await this.assets.loadPanorama(variant.panoramaUrl, variant.name);
    this.sphere.setTexture(texture);
    // Per-variant, and re-applied on every show: the sphere is shared across
    // rooms and variants, so a leftover offset from the last variant would
    // rotate the next one. Absent means 0, which is the identity shift.
    this.sphere.setYawOffset(variant.yawOffsetDeg ?? 0);
    this.sphere.entity.enabled = true;
    if (isFallback) {
      console.info(`[panorama-renderer] using procedural fallback for ${variant.panoramaUrl}`);
    }
  }

  /**
   * Live preview while the editor's alignment slider is being dragged.
   *
   * Separate from show() so a drag costs one material update instead of a
   * texture reload per frame. The committed value still travels on the variant
   * and is re-applied by show().
   */
  setYawOffset(degrees: number): void {
    this.sphere.setYawOffset(degrees);
  }

  hide(): void {
    this.sphere.entity.enabled = false;
  }

  setTint(r: number, g: number, b: number): void {
    this.sphere.setEmissiveTint(r, g, b);
  }

  resetTint(): void {
    this.sphere.resetTint();
  }

  destroy(): void {
    this.sphere.destroy();
  }
}
